/**
 * Compact device list for the session detail page. Each row is tappable to
 * navigate to the per-device page. [+ Add device] renders per `canAddDevice`:
 * while the list is still editable — which for in-store includes
 * `intake_in_progress`, since a wall check-in is created with no devices at all
 * and the customer scans the TV while staff are still holding the machine — and
 * also on any non-terminal session that has no devices yet, which is the same
 * wall check-in after the customer accepted the terms first.
 * On-system styling (sm4rt-web-ui skill): row cards, shared Chip, Tabler icons.
 */

import React from 'react';
import {
  IconExternalLink,
  IconChevronRight,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import type { CheckInDraftJob, CheckInSession } from '@techgitter/contracts';

import { Chip, type ChipTone } from '../../chip';
import {
  canAddDevice,
  canDeleteDevice,
  deviceTicketRef,
  formatAud,
} from './sessionDetailHelpers';
import { DeviceTypeIcon, checkInDeviceLabel } from '../deviceTypes';

export interface CheckInDeviceListSectionProps {
  session: CheckInSession;
  onOpenDevice: (jobId: string) => void;
  onAddDevice?: () => void;
  /**
   * Asks the page to confirm removing this device. Omitted on surfaces with no
   * remove affordance; a row also hides it for itself once the device has a
   * RepairShopr ticket (`canDeleteDevice`).
   *
   * The label is composed here and handed over rather than rebuilt by the
   * caller, so the confirmation names the device exactly as the row does — and
   * so the device-type label map is not copied a tenth time.
   */
  onRemoveDevice?: (job: CheckInDraftJob, label: string) => void;
}



const INSPECTION_LABEL: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

const INSPECTION_TONE: Record<string, ChipTone> = {
  not_started: 'neutral',
  in_progress: 'warning',
  complete: 'success',
};

export const CheckInDeviceListSection: React.FC<CheckInDeviceListSectionProps> = ({
  session,
  onOpenDevice,
  onAddDevice,
  onRemoveDevice,
}) => {
  const canAdd = canAddDevice(session) && onAddDevice;
  const draftJobs = session.draftJobs ?? [];

  return (
    <div className="ci-devices">
      {draftJobs.map((job, idx) => (
        <DeviceRow
          key={job.id}
          index={idx}
          job={job}
          onOpen={() => onOpenDevice(job.id)}
          onRemove={
            onRemoveDevice && canDeleteDevice(session, job)
              ? () => onRemoveDevice(job, deviceLabel(idx, job))
              : undefined
          }
        />
      ))}

      {draftJobs.length === 0 && (
        <div className="ci-device-empty">
          {canAdd
            ? 'No devices on this check-in yet — add the machine the customer handed over.'
            : 'No devices on this check-in.'}
        </div>
      )}

      {canAdd && (
        <button type="button" className="ci-btn ci-btn--ghost ci-btn--block" onClick={onAddDevice}>
          <IconPlus size={15} stroke={1.5} />
          Add device
        </button>
      )}
    </div>
  );
};

/** "Device 2 · Laptop — Won't boot": what the row shows, for the dialog. */
function deviceLabel(index: number, job: CheckInDraftJob): string {
  const base = `Device ${index + 1} · ${checkInDeviceLabel(job)}`;
  return job.serviceSummary ? `${base} — ${job.serviceSummary}` : base;
}

const DeviceRow: React.FC<{
  index: number;
  job: CheckInDraftJob;
  onOpen: () => void;
  onRemove?: () => void;
}> = ({
  index,
  job,
  onOpen,
  onRemove,
}) => {
  const total = (job.preliminaryCharges ?? []).reduce((s, c) => s + (c.finalTotal || 0), 0);
  const ticket = deviceTicketRef(job);
  // The row is a button that opens the device, so the remove control has to be
  // a SIBLING of it, not a child — a button inside a button is invalid markup
  // and the inner one does not reliably receive its own clicks.
  return (
    <div className="ci-device-row">
    <button type="button" className="ci-device" onClick={onOpen} aria-label={`Open device ${index + 1}`}>
      <span className="ci-device__icon"><DeviceTypeIcon deviceType={job.deviceType} size={18} /></span>
      <span className="ci-device__main">
        <span className="ci-device__title">
          Device {index + 1} · {checkInDeviceLabel(job)}
          {job.serviceSummary && <span className="ci-device__summary"> — {job.serviceSummary}</span>}
        </span>
        <span className="ci-device__meta">
          <span className="ci-device__charge">{formatAud(total)}</span>
          <Chip tone={INSPECTION_TONE[job.inspectionStatus] ?? 'neutral'}>
            {INSPECTION_LABEL[job.inspectionStatus] ?? job.inspectionStatus}
          </Chip>
          {/*
            The ticket, by the number a person can quote, linking into sm4rt —
            with RepairShopr kept as a second, unlabelled chip so the deep link
            isn't lost and the number isn't printed twice. A device with no
            stored number can only reach RepairShopr: see `deviceTicketRef`.
          */}
          {ticket && (
            <>
              <Chip
                tone="info"
                href={ticket.sm4rtHref ?? ticket.rsHref}
                onClick={e => e.stopPropagation()}
                title={
                  ticket.sm4rtHref
                    ? 'Open the ticket in sm4rt'
                    : 'Open the ticket in RepairShopr — no RS ticket number on this device'
                }
              >
                {ticket.sm4rtHref ? `#${ticket.label}` : `RS #${ticket.label}`}
              </Chip>
              {ticket.sm4rtHref && (
                <Chip
                  tone="neutral"
                  href={ticket.rsHref}
                  onClick={e => e.stopPropagation()}
                  title="Open the ticket in RepairShopr"
                >
                  <IconExternalLink size={11} stroke={1.5} />
                  RS
                </Chip>
              )}
            </>
          )}
        </span>
      </span>
      <IconChevronRight className="ci-device__chev" size={18} stroke={1.5} />
    </button>
    {onRemove && (
      <button
        type="button"
        className="ci-device__remove"
        onClick={onRemove}
        aria-label={`Remove device ${index + 1}`}
        title="Remove this device from the check-in"
      >
        <IconTrash size={15} stroke={1.5} />
      </button>
    )}
    </div>
  );
};
